import {
  select,
  scaleLog,
  axisLeft,
  transition,
  axisBottom,
  min,
  max,
  forceX,
  forceY,
  forceSimulation,
  format,
  forceCollide,
  scaleSequentialLog,
  scaleOrdinal,
} from "d3";
import {
  debounceTime,
  tap,
  filter,
  map,
  takeUntil,
  auditTime,
  distinctUntilChanged,
} from "rxjs/operators";
import { combineLatest, merge } from "rxjs";
import { interpolateCool } from "d3-scale-chromatic";
import { TAG_TITLES, SEPERATOR } from "../../constants/tagConstants";
import "./beeswarm.scss";

function beeswarm({
  data$,
  element,
  xAxisEl,
  resize$,
  highlight$,
  select$,
  yAxis$,
  destroyed$,
  getX,
  getSize,
  getColor,
  onLegendUpdated,
  margin,
  isOpacityScale$,
  onClick,
}) {
  const yScale = scaleOrdinal();
  const xScale = scaleLog();
  const opacityScale = scaleLog();
  const colorScale = scaleSequentialLog(interpolateCool);
  let radiusScale = (x) => Math.sqrt(x);
  const getY = (x) => x.tag[yAxis$.value];
  let minVal = 0;
  let maxVal = 0;
  let simulation;
  let rMultiplier = 1;
  function getSvg() {
    return select(element).select("svg");
  }

  function getClass(x) {
    return `a${(x || "").toLowerCase().replace(/[^a-zA-Z0-9-]/g, "")}`;
  }

  function getXAxisSvg() {
    return select(xAxisEl).select("svg");
  }

  function drawContainer() {
    const existingSvg = getSvg();

    if (existingSvg.empty()) {
      const svg = select(element).append("svg").attr("class", "bees-chart");
      const xAxisSvg = select(xAxisEl)
        .append("svg")
        .attr("class", "bees-chart-x-axis");

      const axisContainer = svg.append("g").attr("class", "axis-container");
      axisContainer.append("g").attr("class", "axis y-l");
      axisContainer.append("g").attr("class", "grid-x");
      axisContainer.append("g").attr("class", "grid-y");

      const xAxisContainer = xAxisSvg
        .append("g")
        .attr("class", "axis-container-x");
      xAxisContainer.append("g").attr("class", "axis x-b");

      svg.append("g").attr("class", "chart-container");
    }
  }

  function getXAxisDomain() {
    const data = data$.value;
    const xAxisMin = min(data, getX);
    const xAxisMax = max(data, getX);
    const xDiff = xAxisMax - xAxisMin;
    const padding = 0.2 * xDiff;
    return [xAxisMin - padding, xAxisMax + padding];
  }

  function getYaxisFields(yAxisVal, data) {
    const categoryDict = {};
    if (yAxisVal === "tagType") {
      for (let i = 0; i < data.length; i++) {
        const datum = data[i];
        const key = datum.tag[yAxisVal];
        categoryDict[key] = (categoryDict[key] || 0) + datum.totalCount;
      }
      return Object.entries(categoryDict).sort((a, b) => b[1] - a[1]);
    }

    if (yAxisVal === "typeKey") {
      for (let i = 0; i < data.length; i++) {
        const datum = data[i].tag;
        const key = datum[yAxisVal];
        categoryDict[key] = Math.max(categoryDict[key] || 0, datum.count);
      }
      return Object.entries(categoryDict).sort((a, b) => b[1] - a[1]);
    }

    return [];
  }

  function getYAxisDomain(data) {
    let aggr = 0;
    const yAxisVal = yAxis$.value;
    const fakeFirst = [" ", 0];
    const fakeLast = ["  ", 0];
    const tickHeightCategory = (x) => Math.max(radiusScale(x) / 3 + 20);
    const tickHeightId = (x) => Math.max(2 * radiusScale(x) + 10);
    const getHeight =
      yAxisVal === "tagType" ? tickHeightCategory : tickHeightId;

    const labels = getYaxisFields(yAxisVal, data);

    const ticks = [fakeFirst, ...labels.filter((x) => x[1]), fakeLast];

    radiusScale = (x) => Math.sqrt(x) * 3;

    const domain = ticks.map((x) => x[0]);
    const range = ticks
      .map((x) => x[1])
      .map(getHeight)
      .map((x) => (aggr += x) - 0.5 * x);

    return { range, domain };
  }

  function getColorDomain() {
    const data = data$.value;
    minVal = min(data, getColor);
    maxVal = max(data, getColor);
    return [maxVal * 1.8, minVal];
  }

  function getOpacityDomain() {
    const data = data$.value;
    minVal = min(data, getColor);
    maxVal = max(data, getColor);
    return [minVal, maxVal];
  }

  function recalcScales({ width, height }) {
    const data = data$.value;
    const { top, left, bottom, right } = margin;
    const horizontalSpace = width - left - right;

    colorScale.domain(getColorDomain(data));
    opacityScale.domain(getOpacityDomain(data));

    const { range, domain } = getYAxisDomain(data);

    const maxRange = range[range.length - 1];
    const minHeight = height - top - bottom;

    const fixedRange =
      maxRange < minHeight
        ? range.map((x) => x * (minHeight / maxRange))
        : range;

    opacityScale.range([0.6, 1]);
    yScale.range(fixedRange).domain(domain);
    xScale.range([0, horizontalSpace]).domain(getXAxisDomain(data));

    return { width, height: fixedRange[fixedRange.length - 1] + top + bottom };
  }

  function truncate(text, limit) {
    if (text && text.length > limit) {
      return `${text.slice(0, limit)}...`;
    }
    return text;
  }

  function adjustAxisToSize({ width, height }) {
    const duration = 500;
    const { left, top, right, bottom } = margin;
    const bAxisYPosition = 0;
    const bAxisXPosition = left;
    const lAxisYPosition = top;
    const lAxisXPosition = left;
    const svg = getSvg();
    const xAxisSvg = getXAxisSvg();
    const [start, end] = xScale.domain();
    const yearDiff = end - start;

    svg
      .attr("width", width - 30)
      .attr("height", height - 5)
      .on("click", (e) => {
        if (!e.target.classList.contains("point")) onClick(null);
      })
      .on("mousemove", (e) => {
        if (!e.target.classList.contains("point")) highlight$.next(null);
      });

    svg
      .select(".chart-container")
      .attr("width", width - left - right)
      .attr("height", height - top - bottom)
      .attr("transform", `translate(${left}, ${top})`);

    svg
      .select(".y-l")
      .attr("transform", `translate(${lAxisXPosition}, ${lAxisYPosition})`)
      .transition(transition().duration(duration))
      .call(
        axisLeft(yScale)
          .tickSize(0)
          .tickPadding(10)
          .tickFormat((d) => {
            const [tagType, ...rest] = d.split(SEPERATOR);
            const name = rest.join(SEPERATOR);
            const cat = TAG_TITLES[tagType] || tagType;
            const label = yAxis$.value === "typeKey" ? name : cat;
            return truncate(TAG_TITLES[label] || label, 25);
          })
      )

      .selectAll("text")
      .attr("data-tooltip", (d) => {
        const [category, ...rest] = d.split(SEPERATOR);
        const name = rest.join(SEPERATOR);
        const cat = TAG_TITLES[category] || category;
        const tagType = TAG_TITLES[category] || cat;
        return name ? `${name}, ${tagType}` : tagType;
      });

    xAxisSvg
      .select(".x-b")
      .attr("transform", `translate(${bAxisXPosition}, ${bAxisYPosition})`)
      .transition(transition().duration(duration))
      .call(
        axisBottom(xScale)
          .tickSize(0)
          .tickFormat(format("d"))
          .ticks(Math.max(1, Math.min(6, yearDiff)))
      );

    svg
      .select(".grid-y")
      .attr("transform", `translate(${margin.left},${height - margin.bottom})`)
      .call(
        axisBottom(xScale)
          .tickSize(-height + margin.top + margin.bottom)
          .tickFormat("")
          .tickPadding(10)
      );

    svg
      .select(".grid-x")
      .attr("transform", `translate(${left},${top})`)
      .call(
        axisLeft(yScale)
          .tickSize(-width + left + right)
          .tickFormat("")
      );

    const legendScale = scaleLog()
      .domain([minVal, maxVal])
      .range([minVal, maxVal]);

    const ticks = legendScale.ticks();

    onLegendUpdated(ticks.map((x) => [x, colorScale(x)]));
  }

  function adjustDataToScales(keepPrevious) {
    data$.value.forEach((item) => {
      item.x = keepPrevious && item.x ? item.x : xScale(getX(item));
      item.y = keepPrevious && item.y ? item.y : yScale(getY(item));
      item.xPos = xScale(getX(item));
      item.yPos = yScale(getY(item));
      item.r = radiusScale(getSize(item));
    });
  }

  const getHueFilter = (d) => {
    if (isOpacityScale$.value) {
      const { categoryIndex: i, categoryTotal: total } = d;
      const rotation = ((total - i) / total) * 30;
      return `hue-rotate(${rotation}deg)`;
    }
    return "unset";
  };

  function drawNodes() {
    const isOpacity = isOpacityScale$.value;
    const colorField = isOpacity ? "opacity" : "fill";
    const scale = isOpacity ? () => 1 : colorScale;
    const nodes = select(".chart-container")
      .selectAll(".point")
      .data(data$.value, (d) => d.pointId);

    nodes.exit().transition().attr("r", 0).remove();

    nodes
      .transition()
      .attr("cx", (d) => d.x)
      .attr("cy", (d) => d.y)
      .attr("r", (d) => {
        const r = d.r * rMultiplier;
        return Number.isNaN(r) ? 0 : r;
      })
      .style("filter", (d) => getHueFilter(d))
      .attr(colorField, (d) => scale(getColor(d)));

    nodes
      .enter()
      .append("circle")
      .attr(
        "class",
        (d) =>
          `point 
          ${getClass(d.tag.key)} 
          ${isOpacity ? d.fillClass : ""} 
          ${getClass(d.tag.normalized)}
          ${getClass(d.tag.tagType)}`
      )
      .attr("r", (d) => {
        const r = d.r * rMultiplier;
        return Number.isNaN(r) ? 0 : r;
      })
      .attr("cx", (d) => d.xPos)
      .attr("cy", (d) => d.yPos)
      .style("cursor", "pointer")
      .attr("data-tooltip", (d) => d.tag.normalized)
      .style("filter", (d) => getHueFilter(d))
      .attr(colorField, (d) => scale(getColor(d)))
      .on("mouseenter", (_, d) => highlight$.next(d))
      .on("click", (_, d) => onClick(d));
  }

  function simulate() {
    rMultiplier = data$.value?.length < 50 ? 1.5 : 1;

    let prev = 0;
    let counter = 0;

    const fx = forceX()
      .strength(0.15)
      .x((d) => d.xPos);

    const fy = forceY()
      .strength(0.15)
      .y((d) => d.yPos);

    const fc = forceCollide()
      .strength(0.95)
      .radius((d) => d.r * rMultiplier + 0.5);

    const setFc = () => {
      fc.radius((d) => d.r * rMultiplier + 0.5);
    };

    simulation = forceSimulation()
      .force("forceX", fx)
      .force("forceY", fy)
      .force("collide", fc)
      .nodes(data$.value)
      .on("tick", () => {
        if (counter % 100 === 0) {
          if (data$.value.find((x) => x.x < 10 || x.y < 10)) {
            rMultiplier *= 0.7;
            simulation.alpha(0.5).restart();
            setFc();
          }
        }
        const newDate = new Date();
        counter += 1;
        if (newDate - prev > 500) {
          prev = newDate;
          drawNodes();
        }
      });
  }

  function init() {
    select$
      .pipe(
        takeUntil(destroyed$),
        debounceTime(200),
        tap(() => getSvg().selectAll(".selected").classed("selected", 0)),
        map((x) => x?.key),
        filter(Boolean),
        tap((x) => {
          getSvg()
            .selectAll(`.${getClass(x)}`)
            .classed("selected", 1);
        })
      )
      .subscribe(() => {});

    merge(select$, highlight$.pipe(filter(() => !select$.value)))
      .pipe(
        takeUntil(destroyed$),
        auditTime(300),
        tap(() => getSvg().selectAll(".highlighted").classed("highlighted", 0)),
        tap(() => getSvg().classed("highlighted-node", 0)),
        filter(Boolean),
        map((x) => getClass(x.normalized)),
        tap(() => getSvg().classed("highlighted-node", 1)),
        tap((x) => getSvg().selectAll(`.${x}`).classed("highlighted", 1))
      )
      .subscribe(() => {});

    combineLatest([
      isOpacityScale$.pipe(
        distinctUntilChanged(),
        tap(() => getSvg().selectAll(".point").remove())
      ),
      data$,
      yAxis$,
      resize$.pipe(
        debounceTime(1000),
        filter(() => element.parentNode),
        map(() => element.parentNode.getBoundingClientRect()),
        distinctUntilChanged(
          (a, b) => a.width === b.width && a.height === b.height
        )
      ),
    ])
      .pipe(
        takeUntil(destroyed$),
        debounceTime(100),
        tap(() => onClick(null)),
        tap(() => highlight$.next(null)),
        tap(() => simulation && simulation.stop()),
        filter(() => element.parentNode),
        map(() => element.parentNode.getBoundingClientRect()),
        map((bBox) => recalcScales(bBox)),
        tap((bBox) => adjustAxisToSize(bBox)),
        tap(() => adjustDataToScales(yAxis$.value !== "typeKey")),
        tap(() => drawNodes()),
        filter(() => yAxis$.value !== "typeKey"),
        tap(() => simulate()),
        debounceTime(15000),
        tap(() => simulation && simulation.stop())
      )
      .subscribe(() => {});
  }

  init();
  drawContainer();
}

export default beeswarm;
